import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard, faLock, faTriangleExclamation, faCheckCircle,
  faArrowLeft, faSpinner, faShieldAlt, faWrench, faEnvelope, faRotateRight,
  faUniversity
} from '@fortawesome/free-solid-svg-icons';
import { Turnstile } from '@marsidev/react-turnstile';
import type { TurnstileInstance } from '@marsidev/react-turnstile';

// ====================================
// Types
// ====================================
interface LoginFormProps {
  mode: 'merchant' | 'admin' | 'reseller' | 'agent';
  onLoginSuccess?: () => void;
}

interface SystemSettings {
  system_name?: string;
  system_logo_url?: string;
  turnstile_site_key?: string;
}

// ตรวจสอบว่า input เป็น email หรือไม่
const isEmail = (input: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
};

// ====================================
// Shared Login Form Component
// ====================================
const LoginForm: React.FC<LoginFormProps> = ({ mode, onLoginSuccess }) => {
  const { t } = useTranslation();

  // Mode-specific config
  const config = {
    merchant: {
      title: 'Payment Gateway',
      subtitle: 'Merchant Portal',
      inputLabel: 'Email',
      inputPlaceholder: 'email@example.com',
      inputType: 'email' as const,
      loginButtonText: t('auth.loginButton'),
      redirectPath: '/',
      defaultIcon: faCreditCard,
      apiEndpoints: {
        primaryLogin: '/auth/merchant/login',
        employeeLogin: '/auth/employee/login',
        primary2FA: '/auth/merchant/verify-2fa',
        employee2FA: '/auth/employee/verify-2fa',
        primaryVerifyEmail: '/auth/merchant/verify-email',
        primaryResendEmailVerify: '/auth/merchant/resend-email-verify',
        primaryRequestEmailChange: '/auth/merchant/request-email-change',
        primaryConfirmEmailChange: '/auth/merchant/confirm-email-change',
        employeeVerifyEmail: '/auth/employee/verify-email',
        employeeResendEmailVerify: '/auth/employee/resend-email-verify',
        employeeRequestEmailChange: '/auth/employee/request-email-change',
        employeeConfirmEmailChange: '/auth/employee/confirm-email-change',
      }
    },
    admin: {
      title: 'Admin Center',
      subtitle: 'Payment Gateway Management',
      inputLabel: 'Email / Username',
      inputPlaceholder: t('auth.adminPlaceholder'),
      inputType: 'text' as const,
      loginButtonText: t('auth.loginButton'),
      redirectPath: '/admin',
      defaultIcon: faWrench,
      apiEndpoints: {
        primaryLogin: '/auth/admin/login',
        employeeLogin: '/auth/admin-employee/login',
        primary2FA: '/auth/admin/verify-2fa',
        employee2FA: '/auth/admin-employee/verify-2fa',
      }
    },
    reseller: {
      title: 'Reseller Portal',
      subtitle: 'Reseller Management Center',
      inputLabel: 'Email',
      inputPlaceholder: 'email@example.com',
      inputType: 'email' as const,
      loginButtonText: t('auth.loginButton'),
      redirectPath: '/reseller',
      defaultIcon: faCreditCard,
      apiEndpoints: {
        primaryLogin: '/auth/reseller/login',
        employeeLogin: '/auth/reseller/login',
        primary2FA: '/auth/reseller/login',
        employee2FA: '/auth/reseller/login',
      }
    },
    agent: {
      title: 'Agent Portal',
      subtitle: 'Commission Report Center',
      inputLabel: 'อีเมล',
      inputPlaceholder: 'agent@example.com',
      inputType: 'email' as const,
      loginButtonText: t('auth.loginButton'),
      redirectPath: '/agent',
      defaultIcon: faUniversity,
      apiEndpoints: {
        primaryLogin: '/agent/auth/login',
        employeeLogin: '/agent/auth/login',
        primary2FA: '/agent/auth/login',
        employee2FA: '/agent/auth/login',
      }
    }
  };

  const cfg = config[mode];

  // ====================================
  // State
  // ====================================
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [otp, setOtp] = useState('');

  // IDs for 2FA
  const [primaryId, setPrimaryId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [sessionConflict, setSessionConflict] = useState<any>(null);
  const [is2FAForEmployee, setIs2FAForEmployee] = useState(false);

  // Email Verify state (ครั้งแรก — ก่อน login)
  const [showEmailVerify, setShowEmailVerify] = useState(false);
  const [emailVerifyToken, setEmailVerifyToken] = useState('');
  const [emailVerifyHint, setEmailVerifyHint] = useState('');
  const [emailVerifyCode, setEmailVerifyCode] = useState('');
  const [isEmailVerifyForEmployee, setIsEmailVerifyForEmployee] = useState(false);
  const [emailVerifyCooldown, setEmailVerifyCooldown] = useState(0);
  // Change email sub-flow
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [changeEmailStep, setChangeEmailStep] = useState<'input' | 'otp'>('input');
  const [changeEmailOtp, setChangeEmailOtp] = useState('');
  const [changeEmailHint, setChangeEmailHint] = useState('');

  const [systemSettings, setSystemSettings] = useState<SystemSettings>({});
  const navigate = useNavigate();

  // 🔒 Turnstile CAPTCHA
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileReady, setTurnstileReady] = useState(false);

  // ====================================
  // Load system settings
  // ====================================
  useEffect(() => {
    loadSystemSettings();
    
    // Check session expired
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired')) {
      toast.error(t('auth.sessionExpired'), {
        toastId: 'session-expired'
      });
      const basePath = mode === 'admin' ? '/admin/login' : '/login';
      window.history.replaceState({}, document.title, basePath);
    }
  }, [mode]);

  const loadSystemSettings = async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      if (response.data.success && response.data.data) {
        const settings: SystemSettings = {};
        response.data.data.forEach((item: { setting_key: string; setting_value: string }) => {
          if (item.setting_key === 'system_name') settings.system_name = item.setting_value;
          if (item.setting_key === 'system_logo_url') settings.system_logo_url = item.setting_value;
          if (item.setting_key === 'turnstile_site_key') settings.turnstile_site_key = item.setting_value;
        });
        setSystemSettings(settings);
      }
    } catch (error) {
      console.warn('Could not load system settings');
    }
  };

  // 🔒 Turnstile handlers
  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    setTurnstileReady(true);
  };

  const handleTurnstileError = () => {
    setTurnstileToken('');
    setTurnstileReady(false);
    toast.error(t('auth.captchaFailed'));
    turnstileRef.current?.reset();
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken('');
    setTurnstileReady(false);
    turnstileRef.current?.reset();
  };

  // ====================================
  // Handle Login - Auto-detect user type
  // ====================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 🔒 ตรวจสอบ Turnstile token (ถ้าเปิดใช้งาน)
    if (systemSettings.turnstile_site_key && !turnstileToken) {
      toast.error(t('auth.waitCaptcha'));
      return;
    }

    setLoading(true);

    try {
      if (mode === 'merchant') {
        await tryMerchantLogin();
      } else if (mode === 'reseller') {
        await tryResellerLogin();
      } else if (mode === 'agent') {
        await tryAgentLogin();
      } else {
        if (isEmail(identifier)) {
          await tryAdminEmployeeLogin();
        } else {
          await tryAdminLogin();
        }
      }
    } catch (error: any) {
      handleLoginError(error);
      // Reset Turnstile after failed login
      turnstileRef.current?.reset();
      setTurnstileToken('');
      setTurnstileReady(false);
    } finally {
      setLoading(false);
    }
  };

  // ====================================
  // Merchant Mode Login
  // ====================================
  const tryMerchantLogin = async () => {
    try {
      const response = await merchantApi.post(cfg.apiEndpoints.primaryLogin, {
        email: identifier,
        password,
        turnstile_token: turnstileToken // 🔒 ส่ง Turnstile token
      });

      if (response.data.data?.requires_email_verify) {
        setShowEmailVerify(true);
        setEmailVerifyToken(response.data.data.temp_token);
        setEmailVerifyHint(response.data.data.email_hint || '');
        setIsEmailVerifyForEmployee(false);
        toast.info('กรุณายืนยันอีเมลก่อนเข้าสู่ระบบ');
        return;
      }

      if (response.data.data?.requires_2fa) {
        setShow2FA(true);
        setPrimaryId(response.data.data.merchant_id);
        setIs2FAForEmployee(false);
        if (response.data.data.session_conflict) {
          setSessionConflict(response.data.data.existing_session);
          toast.warning(t('auth.sessionConflict'));
          return;
        }
        toast.info(t('auth.enterOtp'));
        return;
      }

      handleLoginSuccess(response.data.data);
    } catch (merchantError: any) {
      try {
        const response = await merchantApi.post(cfg.apiEndpoints.employeeLogin, {
          email: identifier,
          password,
          turnstile_token: turnstileToken
        });

        if (response.data.data?.requires_email_verify) {
          setShowEmailVerify(true);
          setEmailVerifyToken(response.data.data.temp_token);
          setEmailVerifyHint(response.data.data.email_hint || '');
          setIsEmailVerifyForEmployee(true);
          toast.info('กรุณายืนยันอีเมลก่อนเข้าสู่ระบบ');
          return;
        }

        if (response.data.data?.requires_2fa) {
          setShow2FA(true);
          setEmployeeId(response.data.data.employee_id);
          setPrimaryId(response.data.data.merchant_id);
          setIs2FAForEmployee(true);
          toast.info(t('auth.enterOtp'));
          return;
        }

        handleLoginSuccess(response.data.data);
      } catch (employeeError: any) {
        throw merchantError;
      }
    }
  };

  // ====================================
  // Reseller Mode Login
  // ====================================
  const tryResellerLogin = async () => {
    const response = await merchantApi.post(cfg.apiEndpoints.primaryLogin, {
      email: identifier,
      password,
      turnstile_token: turnstileToken
    });

    handleLoginSuccess(response.data.data);
  };

  // ====================================
  // Agent Mode Login
  // ====================================
  const tryAgentLogin = async () => {
    const response = await merchantApi.post(cfg.apiEndpoints.primaryLogin, {
      email: identifier,
      password
    });
    handleLoginSuccess(response.data.data);
  };

  // ====================================
  // Admin Mode Login
  // ====================================
  const tryAdminLogin = async () => {
    const response = await merchantApi.post(cfg.apiEndpoints.primaryLogin, {
      username: identifier,
      password,
      turnstile_token: turnstileToken // 🔒 ส่ง Turnstile token
    });

    if (response.data.data?.requires_2fa) {
      setShow2FA(true);
      setPrimaryId(response.data.data.admin_id);
      setIs2FAForEmployee(false);
      toast.info(t('auth.enterOtp'));
      return;
    }

    handleLoginSuccess(response.data.data);
  };

  const tryAdminEmployeeLogin = async () => {
    const response = await merchantApi.post(cfg.apiEndpoints.employeeLogin, {
      email: identifier,
      password,
      turnstile_token: turnstileToken // 🔒 ส่ง Turnstile token
    });

    if (response.data.requires_2fa) {
      setShow2FA(true);
      setEmployeeId(response.data.admin_employee_id);
      setSessionToken(response.data.session_token);
      setIs2FAForEmployee(true);
      toast.info(t('auth.enterOtp'));
      return;
    }

    handleLoginSuccess(response.data.data);
  };

  // ====================================
  // Handle Login Error
  // ====================================
  const handleLoginError = (error: any) => {
    if (error.requires_2fa) {
      setShow2FA(true);
      setPrimaryId(error.merchant_id);
      setIs2FAForEmployee(false);
      if (error.session_conflict) {
        setSessionConflict(error.existing_session);
        toast.warning(t('auth.sessionConflict'));
      } else {
        toast.info(t('auth.enterAuthOtp'));
      }
      return;
    }
    toast.error(error.response?.data?.message || error.message || t('auth.loginFailed'));
  };

  // ====================================
  // Handle Login Success
  // ====================================
  const handleLoginSuccess = (data: any) => {
    if (mode === 'admin') {
      const user = data.user || data.employee;
      if (user) {
        localStorage.setItem('admin_user', JSON.stringify(user));
      }
    }

    if (mode === 'reseller') {
      const reseller = data.reseller;
      if (reseller) {
        localStorage.setItem('reseller_user', JSON.stringify(reseller));
      }
    }

    if (mode === 'agent') {
      localStorage.setItem('agent_user', JSON.stringify(data));
    }

    toast.success(t('auth.loginSuccess'));

    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      if (mode === 'admin' || mode === 'reseller' || mode === 'agent') {
        navigate(cfg.redirectPath);
      } else {
        window.location.href = cfg.redirectPath;
      }
    }
  };

  // ====================================
  // Handle 2FA
  // ====================================
  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;

      if (is2FAForEmployee) {
        const endpoint = cfg.apiEndpoints.employee2FA;
        
        if (mode === 'admin') {
          response = await merchantApi.post(endpoint, {
            admin_employee_id: employeeId,
            session_token: sessionToken,
            otp
          });
        } else {
          response = await merchantApi.post(endpoint, {
            employee_id: employeeId,
            merchant_id: primaryId,
            otp
          });
        }
      } else {
        const endpoint = cfg.apiEndpoints.primary2FA;
        
        if (mode === 'admin') {
          response = await merchantApi.post(endpoint, {
            admin_id: primaryId,
            otp,
            is_employee: false
          });
        } else {
          response = await merchantApi.post(endpoint, {
            merchant_id: primaryId,
            otp
          });
        }
      }

      handleLoginSuccess(response.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('auth.otpInvalid'));
    } finally {
      setLoading(false);
    }
  };

  const reset2FA = () => {
    setShow2FA(false);
    setOtp('');
    setPrimaryId('');
    setEmployeeId('');
    setSessionToken('');
    setSessionConflict(null);
    setIs2FAForEmployee(false);
  };

  // ====================================
  // Email Verify Handlers
  // ====================================
  const startCooldownTimer = (setter: React.Dispatch<React.SetStateAction<number>>, seconds: number) => {
    setter(seconds);
    const timer = setInterval(() => {
      setter((prev) => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
    }, 1000);
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ep = isEmailVerifyForEmployee
        ? (cfg.apiEndpoints as any).employeeVerifyEmail
        : (cfg.apiEndpoints as any).primaryVerifyEmail;
      const response = await merchantApi.post(ep, { temp_token: emailVerifyToken, otp: emailVerifyCode });
      handleLoginSuccess(response.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'รหัส OTP ไม่ถูกต้อง');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmailVerify = async () => {
    if (emailVerifyCooldown > 0) return;
    try {
      const ep = isEmailVerifyForEmployee
        ? (cfg.apiEndpoints as any).employeeResendEmailVerify
        : (cfg.apiEndpoints as any).primaryResendEmailVerify;
      await merchantApi.post(ep, { temp_token: emailVerifyToken });
      toast.success('ส่งรหัส OTP ใหม่แล้ว');
      startCooldownTimer(setEmailVerifyCooldown, 60);
    } catch (err: any) {
      const cd = err.response?.data?.data?.cooldown_seconds;
      if (cd) { startCooldownTimer(setEmailVerifyCooldown, cd); toast.error(`กรุณารอ ${cd} วินาที`); }
      else toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const handleRequestEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ep = isEmailVerifyForEmployee
        ? (cfg.apiEndpoints as any).employeeRequestEmailChange
        : (cfg.apiEndpoints as any).primaryRequestEmailChange;
      const response = await merchantApi.post(ep, { temp_token: emailVerifyToken, new_email: newEmailInput });
      setChangeEmailHint(response.data.data?.email_hint || newEmailInput.replace(/^(.{2}).*(@.*)$/, '$1***$2'));
      setChangeEmailStep('otp');
      toast.success('ส่งรหัส OTP ไปยังอีเมลใหม่แล้ว');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ep = isEmailVerifyForEmployee
        ? (cfg.apiEndpoints as any).employeeConfirmEmailChange
        : (cfg.apiEndpoints as any).primaryConfirmEmailChange;
      const response = await merchantApi.post(ep, { temp_token: emailVerifyToken, otp: changeEmailOtp });
      handleLoginSuccess(response.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'รหัส OTP ไม่ถูกต้อง');
    } finally {
      setLoading(false);
    }
  };

  const resetEmailVerify = () => {
    setShowEmailVerify(false);
    setEmailVerifyToken('');
    setEmailVerifyHint('');
    setEmailVerifyCode('');
    setIsEmailVerifyForEmployee(false);
    setEmailVerifyCooldown(0);
    setShowChangeEmail(false);
    setNewEmailInput('');
    setChangeEmailStep('input');
    setChangeEmailOtp('');
    setChangeEmailHint('');
  };

  // ====================================
  // Render Email Verify Screen (ครั้งแรก)
  // ====================================
  if (showEmailVerify) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#0a0f1e' }}>
        <div className="w-full max-w-sm">

          {/* Header */}
          <div className="mb-8 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-amber-500/20">
              <FontAwesomeIcon icon={faEnvelope} className="text-2xl text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">ยืนยันอีเมลของคุณ</h1>
            <p className="text-gray-400 text-sm">
              ก่อนเข้าสู่ระบบ กรุณายืนยันว่าอีเมล{' '}
              <span className="text-amber-400 font-medium">{emailVerifyHint}</span>{' '}
              เป็นของคุณ
            </p>
          </div>

          {/* Main verify form or change email form */}
          {!showChangeEmail ? (
            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  รหัส OTP ที่ได้รับทางอีเมล
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={emailVerifyCode}
                  onChange={(e) => setEmailVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  className="w-full px-4 py-4 rounded-xl text-center text-3xl font-bold tracking-[0.5rem]
                             bg-white/5 border border-white/10 text-white placeholder-white/20
                             focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': '#f59e0b' } as React.CSSProperties}
                />
              </div>

              <button
                type="submit"
                disabled={loading || emailVerifyCode.length !== 6}
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm
                           disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}
              >
                {loading
                  ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />กำลังตรวจสอบ...</>
                  : <><FontAwesomeIcon icon={faCheckCircle} className="mr-2" />ยืนยันอีเมล</>
                }
              </button>

              <button
                type="button"
                onClick={handleResendEmailVerify}
                disabled={emailVerifyCooldown > 0}
                className="w-full py-3 rounded-xl border border-white/10 text-sm font-medium
                           transition-colors flex items-center justify-center gap-2
                           disabled:opacity-40 disabled:cursor-not-allowed text-gray-400 hover:bg-white/5"
              >
                <FontAwesomeIcon icon={faRotateRight} />
                {emailVerifyCooldown > 0 ? `ส่งใหม่ได้ใน ${emailVerifyCooldown} วินาที` : 'ส่งรหัสใหม่'}
              </button>

              {/* Change email button */}
              <div className="pt-2 border-t border-white/10 text-center">
                <p className="text-gray-500 text-xs mb-2">อีเมลไม่ถูกต้อง?</p>
                <button
                  type="button"
                  onClick={() => setShowChangeEmail(true)}
                  className="text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors"
                >
                  เปลี่ยนอีเมล
                </button>
              </div>

              <button
                type="button"
                onClick={resetEmailVerify}
                className="w-full py-3 rounded-xl border border-white/10 text-gray-500 text-sm
                           hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                กลับไปหน้า Login
              </button>
            </form>
          ) : (
            /* Change Email Sub-flow */
            <div className="space-y-4">
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
                <p className="text-amber-400 text-xs">OTP จะถูกส่งไปยัง<strong>อีเมลใหม่</strong>ที่คุณกรอก</p>
              </div>

              {changeEmailStep === 'input' ? (
                <form onSubmit={handleRequestEmailChange} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      อีเมลใหม่
                    </label>
                    <input
                      type="email"
                      value={newEmailInput}
                      onChange={(e) => setNewEmailInput(e.target.value)}
                      required
                      autoFocus
                      placeholder="newemail@example.com"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 transition-all"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }}
                      onFocus={e => e.currentTarget.style.borderColor = '#f59e0b'}
                      onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !newEmailInput}
                    className="w-full py-3.5 rounded-xl text-white font-semibold text-sm
                               disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}
                  >
                    {loading
                      ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />กำลังส่ง...</>
                      : <><FontAwesomeIcon icon={faEnvelope} className="mr-2" />ส่ง OTP ไปยังอีเมลใหม่</>
                    }
                  </button>
                </form>
              ) : (
                <form onSubmit={handleConfirmEmailChange} className="space-y-4">
                  <p className="text-gray-400 text-sm text-center">
                    กรอกรหัส OTP ที่ส่งไปยัง{' '}
                    <span className="text-amber-400 font-medium">{changeEmailHint}</span>
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={changeEmailOtp}
                    onChange={(e) => setChangeEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    className="w-full px-4 py-4 rounded-xl text-center text-3xl font-bold tracking-[0.5rem]
                               bg-white/5 border border-white/10 text-white placeholder-white/20
                               focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    style={{ '--tw-ring-color': '#f59e0b' } as React.CSSProperties}
                  />
                  <button
                    type="submit"
                    disabled={loading || changeEmailOtp.length !== 6}
                    className="w-full py-3.5 rounded-xl text-white font-semibold text-sm
                               disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}
                  >
                    {loading
                      ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />กำลังยืนยัน...</>
                      : <><FontAwesomeIcon icon={faCheckCircle} className="mr-2" />ยืนยันอีเมลใหม่</>
                    }
                  </button>
                  <button
                    type="button"
                    onClick={() => { setChangeEmailStep('input'); setChangeEmailOtp(''); }}
                    className="w-full py-3 rounded-xl border border-white/10 text-gray-400 text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} /> กรอกอีเมลใหม่อีกครั้ง
                  </button>
                </form>
              )}

              <button
                type="button"
                onClick={() => { setShowChangeEmail(false); setChangeEmailStep('input'); setNewEmailInput(''); setChangeEmailOtp(''); }}
                className="w-full py-3 rounded-xl border border-white/10 text-gray-500 text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faArrowLeft} /> กลับไปยืนยันอีเมลเดิม
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ====================================
  // Render 2FA Form
  // ====================================
  if (show2FA) {
    return (
      <div className="min-h-screen flex" style={{ background: '#0a0f1e' }}>
        {/* Left panel */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
             style={{ background: 'var(--theme-primary-gradient)' }}>
          <div className="absolute inset-0 opacity-10"
               style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
          <div className="relative z-10">
            {systemSettings.system_logo_url
              ? <img src={systemSettings.system_logo_url} alt="Logo" className="h-20 w-auto max-w-[280px] object-contain" />
              : <span className="text-white font-bold text-2xl tracking-wider">{systemSettings.system_name || cfg.title}</span>
            }
          </div>
          <div className="relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-6 border border-white/20">
              <FontAwesomeIcon icon={faShieldAlt} className="text-2xl text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Two-Factor<br/>Authentication</h2>
            <p className="text-white/60 text-sm leading-relaxed">
              Verify your identity with a one-time code to ensure only authorized users can access the system.
            </p>
          </div>
          <p className="relative z-10 text-white/30 text-xs">
            &copy; {new Date().getFullYear()} {systemSettings.system_name || 'Go2Pay'} · Secured & Encrypted
          </p>
        </div>

        {/* Right panel */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                   style={{ background: 'var(--theme-primary-light)' }}>
                <FontAwesomeIcon icon={faShieldAlt} className="text-xl text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">{t('auth.verify2fa')}</h1>
              <p className="text-gray-400 text-sm">
                {sessionConflict ? t('auth.sessionConflictTitle') : t('auth.enterCode')}
              </p>
            </div>

            {sessionConflict && (
              <div className="mb-6 p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/10">
                <p className="text-yellow-400 font-semibold text-sm mb-1 flex items-center gap-2">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> {t('auth.sessionConflictTitle')}
                </p>
                <p className="text-yellow-300/70 text-xs">
                  IP: {sessionConflict.ip} · {new Date(sessionConflict.last_activity).toLocaleString('th-TH')}
                </p>
                <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                  <FontAwesomeIcon icon={faCheckCircle} /> {t('auth.otpToLogout')}
                </p>
              </div>
            )}

            <form onSubmit={handleVerify2FA} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  {t('auth.otpLabel')}
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  required
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  className="w-full px-4 py-4 rounded-xl text-center text-3xl font-bold tracking-[0.5rem]
                             bg-white/5 border border-white/10 text-white placeholder-white/20
                             focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': 'var(--theme-primary)' } as React.CSSProperties}
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: 'var(--theme-primary-gradient)' }}
              >
                {loading ? (
                  <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />{t('auth.verifying')}</>
                ) : (
                  <><FontAwesomeIcon icon={faLock} className="mr-2" />{t('auth.verifyIdentity')}</>
                )}
              </button>

              <button
                type="button"
                onClick={reset2FA}
                className="w-full py-3 rounded-xl border border-white/10 text-gray-400 text-sm font-medium
                           hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                {t('common.back')}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ====================================
  // Render Main Login Form
  // ====================================
  return (
    <div className="min-h-screen flex" style={{ background: '#060b18' }}>
      <style>{`
        @keyframes float1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(30px,-40px) scale(1.05)} 66%{transform:translate(-20px,20px) scale(0.95)} }
        @keyframes float2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-40px,30px) scale(1.08)} 66%{transform:translate(25px,-15px) scale(0.92)} }
        @keyframes float3 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(15px,35px)} }
        @keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes spin-reverse { from{transform:rotate(360deg)} to{transform:rotate(0deg)} }
        @keyframes pulse-glow { 0%,100%{opacity:0.4} 50%{opacity:0.8} }
        @keyframes shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(200%)} }
        @keyframes slide-up { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes rise { 0%{opacity:0;transform:translateY(0) scale(0.5)} 15%{opacity:0.6} 85%{opacity:0.2} 100%{opacity:0;transform:translateY(-180px) scale(1.2)} }
        @keyframes border-rotate { from{--angle:0deg} to{--angle:360deg} }
        @keyframes scan { 0%{transform:translateY(-100%);opacity:0} 10%{opacity:1} 90%{opacity:0.4} 100%{transform:translateY(520px);opacity:0} }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes card-glow { 0%,100%{box-shadow:0 0 80px rgba(0,0,0,0.5),0 0 0 1px rgba(255,255,255,0.06),0 0 40px rgba(99,102,241,0)} 50%{box-shadow:0 0 80px rgba(0,0,0,0.5),0 0 0 1px rgba(255,255,255,0.1),0 0 40px rgba(99,102,241,0.08)} }
        @property --angle { syntax:'<angle>'; initial-value:0deg; inherits:false }
        .orb1{animation:float1 12s ease-in-out infinite}
        .orb2{animation:float2 15s ease-in-out infinite}
        .orb3{animation:float3 10s ease-in-out infinite}
        .globe-ring1{animation:spin-slow 20s linear infinite;transform-origin:center}
        .globe-ring2{animation:spin-reverse 15s linear infinite;transform-origin:center}
        .globe-ring3{animation:spin-slow 25s linear infinite;transform-origin:center}
        .pulse-orb{animation:pulse-glow 3s ease-in-out infinite}
        .btn-shimmer::after{content:'';position:absolute;inset:0;background:linear-gradient(105deg,transparent 40%,rgba(255,255,255,0.25) 50%,transparent 60%);animation:shimmer 2.5s infinite}
        .form-panel{animation:slide-up 0.6s ease-out}
        .scan-beam{animation:scan 6s linear infinite}
        .ticker-inner{animation:ticker 18s linear infinite;white-space:nowrap;display:flex;gap:0}
        .card-glow{animation:card-glow 4s ease-in-out infinite}
        .gradient-text{background:linear-gradient(135deg,#ffffff 0%,rgba(255,255,255,0.7) 50%,var(--theme-primary,#6366f1) 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
        .stat-card:hover{transform:translateY(-2px);background:rgba(255,255,255,0.08)!important;transition:all 0.2s}
        .border-glow-wrap{position:relative;padding:1px;border-radius:1rem;background:conic-gradient(from var(--angle),transparent 70%,rgba(99,102,241,0.5) 80%,rgba(139,92,246,0.6) 85%,rgba(99,102,241,0.5) 90%,transparent 100%);animation:border-rotate 4s linear infinite}
      `}</style>

      {/* ── LEFT BRANDING PANEL ── */}
      <div className="hidden lg:flex lg:w-[52%] flex-col justify-between p-14 relative overflow-hidden"
           style={{ background: 'linear-gradient(135deg, #0d1b3e 0%, #0a1628 50%, #060b18 100%)' }}>

        {/* Animated orbs */}
        <div className="orb1 absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-25 pointer-events-none"
             style={{ background: 'var(--theme-primary)' }} />
        <div className="orb2 absolute bottom-1/3 right-1/4 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none"
             style={{ background: 'var(--theme-secondary)' }} />
        <div className="orb3 absolute top-2/3 left-1/3 w-48 h-48 rounded-full blur-2xl opacity-15 pointer-events-none"
             style={{ background: 'var(--theme-accent)' }} />

        {/* Fine grid */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        {/* Floating particles */}
        {[
          { left: '15%', delay: '0s',  dur: '8s',  size: 3 },
          { left: '28%', delay: '2s',  dur: '11s', size: 2 },
          { left: '42%', delay: '4s',  dur: '9s',  size: 4 },
          { left: '58%', delay: '1s',  dur: '13s', size: 2 },
          { left: '70%', delay: '5s',  dur: '10s', size: 3 },
          { left: '85%', delay: '3s',  dur: '7s',  size: 2 },
          { left: '22%', delay: '6s',  dur: '12s', size: 3 },
          { left: '65%', delay: '7s',  dur: '8s',  size: 2 },
        ].map((p, i) => (
          <div key={i} className="rise absolute bottom-10 rounded-full pointer-events-none"
               style={{ left: p.left, width: p.size, height: p.size, background: 'var(--theme-primary)', opacity: 0.5,
                        animation: `rise ${p.dur} ease-in infinite ${p.delay}` }} />
        ))}

        {/* Globe SVG decoration */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/3 opacity-10 pointer-events-none overflow-hidden" style={{ width: 520, height: 520 }}>
          {/* Scan beam */}
          <div className="scan-beam absolute left-0 right-0 h-px pointer-events-none"
               style={{ background: 'linear-gradient(90deg, transparent, var(--theme-primary), transparent)', opacity: 0.7, top: 0 }} />
          <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="200" cy="200" r="190" stroke="white" strokeWidth="0.6" />
            <circle cx="200" cy="200" r="140" stroke="white" strokeWidth="0.5" />
            <circle cx="200" cy="200" r="90" stroke="white" strokeWidth="0.4" />
            <ellipse className="globe-ring1" cx="200" cy="200" rx="190" ry="60" stroke="white" strokeWidth="0.6" />
            <ellipse className="globe-ring2" cx="200" cy="200" rx="190" ry="100" stroke="white" strokeWidth="0.5" strokeDasharray="4 8" />
            <ellipse className="globe-ring3" cx="200" cy="200" rx="60" ry="190" stroke="white" strokeWidth="0.5" />
            <line x1="10" y1="200" x2="390" y2="200" stroke="white" strokeWidth="0.4" />
            <line x1="200" y1="10" x2="200" y2="390" stroke="white" strokeWidth="0.4" />
            {/* Connection lines between nodes */}
            <line x1="80" y1="200" x2="160" y2="200" stroke="white" strokeWidth="0.3" strokeDasharray="2 4" opacity="0.5" />
            <line x1="200" y1="80" x2="280" y2="160" stroke="white" strokeWidth="0.3" strokeDasharray="2 4" opacity="0.4" />
            <line x1="120" y1="120" x2="200" y2="200" stroke="white" strokeWidth="0.3" strokeDasharray="2 4" opacity="0.4" />
            <line x1="280" y1="280" x2="200" y2="200" stroke="white" strokeWidth="0.3" strokeDasharray="2 4" opacity="0.3" />
            {/* Nodes on equator */}
            {[40,80,120,160,240,280,320,360].map(x => (
              <circle key={`x${x}`} cx={x} cy="200" r="3" fill="white" opacity="0.7">
                <animate attributeName="opacity" values="0.7;1;0.7" dur={`${2 + (x % 5)}s`} repeatCount="indefinite" />
              </circle>
            ))}
            {/* Nodes on meridian */}
            {[40,80,120,160,240,280,320,360].map(y => (
              <circle key={`y${y}`} cx="200" cy={y} r="3" fill="white" opacity="0.7">
                <animate attributeName="opacity" values="0.7;1;0.7" dur={`${1.5 + (y % 4)}s`} repeatCount="indefinite" />
              </circle>
            ))}
            {/* Extra scattered nodes */}
            {[[120,120],[280,120],[320,240],[100,300],[300,300]].map(([cx,cy],i) => (
              <circle key={`s${i}`} cx={cx} cy={cy} r="2.5" fill="white" opacity="0.5">
                <animate attributeName="r" values="2.5;4;2.5" dur={`${3+i}s`} repeatCount="indefinite" />
              </circle>
            ))}
          </svg>
        </div>

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          {systemSettings.system_logo_url ? (
            <img src={systemSettings.system_logo_url} alt="Logo" className="h-20 w-auto max-w-[280px] object-contain" />
          ) : (
            <>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center border border-white/20"
                   style={{ background: 'var(--theme-primary-gradient)' }}>
                <FontAwesomeIcon icon={cfg.defaultIcon} className="text-white text-xl" />
              </div>
              <span className="text-white font-bold text-2xl tracking-wide">{systemSettings.system_name || cfg.title}</span>
            </>
          )}
        </div>

        {/* Center content */}
        <div className="relative z-10 max-w-sm">
          {/* Live indicator */}
          <div className="flex items-center gap-2 mb-6">
            <span className="pulse-orb w-2 h-2 bg-green-400 rounded-full block" />
            <span className="text-green-400 text-xs font-medium tracking-wider uppercase">System Online</span>
          </div>

          <h2 className="gradient-text text-5xl font-bold mb-4 leading-[1.1] tracking-tight">
            {cfg.title}
          </h2>
          <p className="text-white/40 text-base leading-relaxed mb-10">
            {cfg.subtitle} — Enterprise-grade infrastructure for global payment operations.
          </p>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 mb-10">
            {[
              { num: '10M+', label: 'Transactions', icon: '⚡' },
              { num: '500+', label: 'Merchants',    icon: '🏢' },
              { num: '99.9%', label: 'Uptime',      icon: '🛡' },
            ].map(({ num, label, icon }) => (
              <div key={label} className="stat-card rounded-2xl p-3.5 border border-white/10 text-center cursor-default"
                   style={{ background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(12px)', transition: 'all 0.2s' }}>
                <p className="text-lg mb-0.5">{icon}</p>
                <p className="text-xl font-bold text-white">{num}</p>
                <p className="text-white/35 text-[10px] mt-0.5 uppercase tracking-wider">{label}</p>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-2">
            {['256-bit SSL', 'PCI DSS', '2FA Protected', 'ISO 27001'].map((badge) => (
              <span key={badge}
                className="px-3 py-1 rounded-full text-[11px] font-medium border border-white/10 text-white/50"
                style={{ background: 'rgba(255,255,255,0.04)' }}>
                ✓ {badge}
              </span>
            ))}
          </div>
        </div>

        {/* Live ticker */}
        <div className="relative z-10 overflow-hidden rounded-xl border border-white/10 py-2"
             style={{ background: 'rgba(255,255,255,0.03)' }}>
          <div className="ticker-inner text-[10px] text-white/25 font-mono tracking-wider">
            {['TXN #8821 · ฿ 2,400.00 · PAID', 'TXN #8820 · ฿ 850.00 · PAID', 'TXN #8819 · ฿ 15,000.00 · PAID',
              'TXN #8818 · ฿ 500.00 · PAID', 'TXN #8817 · ฿ 3,200.00 · PAID', 'TXN #8816 · ฿ 1,100.00 · PAID'].map((t, i) => (
              <span key={i} className="px-6">&bull;&nbsp;{t}</span>
            ))}
            {/* duplicate for seamless loop */}
            {['TXN #8821 · ฿ 2,400.00 · PAID', 'TXN #8820 · ฿ 850.00 · PAID', 'TXN #8819 · ฿ 15,000.00 · PAID',
              'TXN #8818 · ฿ 500.00 · PAID', 'TXN #8817 · ฿ 3,200.00 · PAID', 'TXN #8816 · ฿ 1,100.00 · PAID'].map((t, i) => (
              <span key={`d${i}`} className="px-6">&bull;&nbsp;{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16 relative overflow-hidden">
        {/* Background glow behind card */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-10"
               style={{ background: 'var(--theme-primary)' }} />
        </div>

        <div className="form-panel w-full max-w-sm relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10 flex items-center gap-3 justify-center">
            {systemSettings.system_logo_url
              ? <img src={systemSettings.system_logo_url} alt="Logo" className="h-16 w-auto max-w-[240px] object-contain" />
              : <span className="text-white font-bold text-2xl">{systemSettings.system_name || cfg.title}</span>
            }
          </div>

          {/* Glassmorphism card with animated border glow */}
          <div className="border-glow-wrap">
          <div className="card-glow rounded-2xl p-8"
               style={{ background: 'rgba(12,16,30,0.95)', backdropFilter: 'blur(24px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>

            {/* Heading */}
            <div className="mb-7">
              <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-1.5">Welcome back</p>
              <h1 className="text-2xl font-bold text-white">Sign in to your account</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
                  {cfg.inputLabel}
                </label>
                <input
                  type={cfg.inputType}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  placeholder={cfg.inputPlaceholder}
                  autoComplete="username"
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 transition-all duration-200"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--theme-primary)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
                  {t('common.password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 transition-all duration-200"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--theme-primary)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>

              {systemSettings.turnstile_site_key && (
                <div className="flex justify-center pt-1">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={systemSettings.turnstile_site_key}
                    onSuccess={handleTurnstileSuccess}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                    options={{ theme: 'dark', size: 'normal', language: 'th', action: mode === 'admin' ? 'admin-login' : 'merchant-login' }}
                  />
                </div>
              )}

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={loading || (systemSettings.turnstile_site_key ? !turnstileReady : false)}
                  className="btn-shimmer relative w-full py-3.5 rounded-xl text-white font-semibold text-sm overflow-hidden
                             disabled:opacity-40 disabled:cursor-not-allowed
                             transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
                  style={{ background: 'var(--theme-primary-gradient)', boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)' }}
                >
                  <span className="relative z-10">
                    {loading
                      ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />{t('auth.loggingIn')}</>
                      : cfg.loginButtonText
                    }
                  </span>
                </button>
              </div>
            </form>

            {/* Divider */}
            <div className="mt-7 pt-6 border-t border-white/5 flex items-center justify-center gap-2">
              <FontAwesomeIcon icon={faShieldAlt} className="text-gray-600 text-xs" />
              <p className="text-gray-600 text-xs">
                {mode === 'merchant'
                  ? t('auth.noAccountContact')
                  : `\u00a9 ${new Date().getFullYear()} ${systemSettings.system_name || 'Go2Pay'} \u00b7 All rights reserved`
                }
              </p>
            </div>
          </div>{/* end card-glow */}
          </div>{/* end border-glow-wrap */}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
